import { useState } from "react";
import { useModal } from "../../../hooks/useModal";
import BasicButton from "../../common/components/BasicButton";
import ListPageTopSection from "../../common/components/ListPageTopSection";
import PageWrapper from "../../common/components/PageWrapper";
import SearchBar from "../../common/components/SearchBar";
import SelectBox from "../../common/components/SelectBox";
import Table from "../../common/components/Table";
import UpdateAdminUserModal from "../components/UpdateAdminUserModal";
import { useForm } from "../../../hooks/useForm";
import { SortDirection } from "global";
import {
  AdminSearchType,
  AdminSortKey,
  useDeleteAdminUserMutation,
  useGetAdminUsersQuery,
} from "../../../lib/apollo/graphql/generated";
import { ADMIN_ROLE_KR, FETCH_SIZE } from "../../../utilities/constants";
import { formatDate } from "../../../utilities/format";
import ActiveStatus from "../../common/components/ActiveStatus";

export default function AdminUsersContainer() {
  const [filters, setFilters, onBatchUpdate] = useForm({
    query: "",
    adminSearchType: AdminSearchType.Name,
    sortKey: AdminSortKey.CreatedAt,
    sortDirection: "DESC" as SortDirection,
  });

  const [selectedAdminId, setSelectedAdminId] = useState<string | null>(null);
  const [isUpdatingAdmin, { open: openUpdateModal, close: closeUpdateModal }] =
    useModal();

  const onUpdate = (adminId?: string) => {
    setSelectedAdminId(adminId || null);
    openUpdateModal();
  };

  const { data, loading, refetch } = useGetAdminUsersQuery({
    variables: {
      first: FETCH_SIZE,
    },
  });

  const admins = data?.getAdminUsers?.edges || [];

  const [deleteAdminUser] = useDeleteAdminUserMutation();

  const onDelete = (adminId: string) => {
    const confirmed = window.confirm("삭제하시겠습니까?");

    if (!confirmed) {
      return;
    }

    deleteAdminUser({
      variables: {
        adminId,
      },
    });
  };

  const onSort = (key?: AdminSortKey, direction?: SortDirection) => {
    const updatedFilters = {
      ...filters,
      sortKey: !key ? AdminSortKey.CreatedAt : key,
      sortDirection: !direction ? "DESC" : (direction as SortDirection),
    };

    onBatchUpdate(updatedFilters);

    refetch({
      ...updatedFilters,
    });
  };

  return (
    <>
      {isUpdatingAdmin && (
        <UpdateAdminUserModal
          adminId={selectedAdminId}
          open={isUpdatingAdmin}
          onClose={closeUpdateModal}
        />
      )}
      <PageWrapper>
        <ListPageTopSection>
          <SelectBox
            value={filters.adminSearchType}
            onSelect={(value) => setFilters("adminSearchType", value)}
            options={[
              { label: "이름", value: AdminSearchType.Name },
              { label: "상태", value: AdminSearchType.Status },
              { label: "권한", value: AdminSearchType.Role },
            ]}
          />
          <SearchBar
            value={filters.query}
            onChange={(value) => setFilters("query", value)}
            onSearch={() => refetch({ ...filters })}
          />
          <div className="ml-auto">
            <BasicButton text="등록" onClick={() => onUpdate()} />
          </div>
        </ListPageTopSection>

        <Table.Wrapper>
          <Table.Header
            sort={{
              key: filters.sortKey,
              direction: filters.sortDirection,
              onSort,
            }}
            tabs={[
              { label: "ID (이메일)", className: "w-full" },
              {
                label: "이름",
                className: "w-[45%]",
                sortKey: AdminSortKey.Name,
              },
              {
                label: "권한",
                className: "w-[45%]",
                sortKey: AdminSortKey.Role,
              },
              {
                label: "상태",
                className: "w-[45%]",
                sortKey: AdminSortKey.Status,
              },
              {
                label: "등록일자",
                className: "w-[45%]",
                sortKey: AdminSortKey.CreatedAt,
              },
              { label: "관리", className: "w-[45%]" },
            ]}
          />
          <Table.Body isLoading={loading} isEmpty={!admins?.length}>
            {admins.map((admin) => (
              <Table.Row>
                <Table.Column className="w-full">{admin.email}</Table.Column>
                <Table.Column className="w-[45%]">{admin.name}</Table.Column>
                <Table.Column className="w-[45%]">
                  {ADMIN_ROLE_KR[admin.adminRole.name]}
                </Table.Column>
                <Table.Column className="w-[45%]">
                  <ActiveStatus status={admin.status} />
                </Table.Column>
                <Table.Column className="w-[45%]">
                  {formatDate(admin.createdAt, "YYYY.MM.DD HH:mm")}
                </Table.Column>
                <Table.RowManageButtons
                  className="w-[45%]"
                  onUpdate={() => onUpdate(admin.id)}
                  onDelete={
                    admin.status === "INACTIVE"
                      ? undefined
                      : () => onDelete(admin.id)
                  }
                />
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Wrapper>
        <Table.Pagination page={1} total={5} onPageChange={() => {}} />
      </PageWrapper>
    </>
  );
}
